/**
 * TrainingHistory Component
 * 
 * Mini view showing the last 7 days of training activity.
 * Displays date, action taken, and representation score for each session.
 */

import React from 'react';
import { Calendar, Check, Edit2, X, TrendingUp } from 'lucide-react';
import type { TrainingHistoryItem, TrainingAction } from '../../types/trainingTypes';
import { getScoreColorClass, getActionLabel } from '../../lib/scoringUtils';
import { getTrainingHistory, getTrainingStats } from '../../lib/trainingStorage';

interface TrainingHistoryProps {
    onRefresh?: () => void;
}

export const TrainingHistory: React.FC<TrainingHistoryProps> = () => {
    const history = getTrainingHistory();
    const stats = getTrainingStats();

    return (
        <div className="card-elevated p-6 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-hig-text-primary flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-hig-text-tertiary" />
                    Training History
                </h3>
                <span className="text-xs text-hig-text-tertiary">Last 7 days</span>
            </div>

            {/* Stats Summary */}
            {stats.totalSessions > 0 && (
                <div className="grid grid-cols-3 gap-2 p-3 bg-hig-bg-secondary rounded-lg border border-hig-border">
                    <StatMini label="Sessions" value={stats.totalSessions.toString()} />
                    <StatMini label="Avg Score" value={stats.avgRepresentationScore.toString()} />
                    <StatMini label="Approval" value={`${stats.approvalRate}%`} />
                </div>
            )}

            {/* History List */}
            {history.length > 0 ? (
                <div className="space-y-2">
                    {history.map((item, idx) => (
                        <HistoryRow key={idx} item={item} />
                    ))}
                </div>
            ) : (
                <div className="text-center py-8">
                    <Calendar className="w-8 h-8 text-hig-text-tertiary mx-auto mb-3" />
                    <p className="text-sm text-hig-text-tertiary">No training sessions yet</p>
                    <p className="text-xs text-hig-text-tertiary mt-1">Complete your first daily training</p>
                </div>
            )}

            {/* Streak Indicator */}
            {history.length > 0 && (
                <div className="pt-4 border-t border-hig-divider">
                    <StreakIndicator history={history} />
                </div>
            )}
        </div>
    );
};

// ============================================
// HISTORY ROW SUB-COMPONENT
// ============================================

interface HistoryRowProps {
    item: TrainingHistoryItem;
}

const HistoryRow: React.FC<HistoryRowProps> = ({ item }) => {
    return (
        <div className="flex items-center gap-3 p-3 bg-hig-bg-secondary rounded-lg border border-hig-border">
            {/* Date */}
            <div className="w-16 text-sm text-hig-text-tertiary">
                {item.date}
            </div>

            {/* Action Icon */}
            <ActionIcon action={item.action} />

            {/* Case Title */}
            <div className="flex-1 text-sm text-hig-text-secondary truncate">
                {item.caseTitle}
            </div>

            {/* Score */}
            <div className={`text-sm font-semibold ${getScoreColorClass(item.representationScore)}`}>
                {item.representationScore}
            </div>
        </div>
    );
};

// ============================================
// ACTION ICON SUB-COMPONENT
// ============================================

interface ActionIconProps {
    action: TrainingAction;
}

const ActionIcon: React.FC<ActionIconProps> = ({ action }) => {
    const iconConfig = {
        approve: {
            icon: <Check className="w-3.5 h-3.5" />,
            bg: 'bg-green-500/20',
            color: 'text-green-400'
        },
        edit_teach: {
            icon: <Edit2 className="w-3.5 h-3.5" />,
            bg: 'bg-yellow-500/20',
            color: 'text-yellow-400'
        },
        reject_explain: {
            icon: <X className="w-3.5 h-3.5" />,
            bg: 'bg-red-500/20',
            color: 'text-red-400'
        }
    };

    const config = iconConfig[action];

    return (
        <div
            className={`w-6 h-6 rounded flex items-center justify-center ${config.bg}`}
            title={getActionLabel(action)}
        >
            <span className={config.color}>{config.icon}</span>
        </div>
    );
};

// ============================================
// STAT MINI SUB-COMPONENT
// ============================================

interface StatMiniProps {
    label: string;
    value: string;
}

const StatMini: React.FC<StatMiniProps> = ({ label, value }) => {
    return (
        <div className="text-center">
            <div className="text-sm font-semibold text-hig-text-primary">{value}</div>
            <div className="text-[10px] text-hig-text-tertiary uppercase tracking-wide">{label}</div>
        </div>
    );
};

// ============================================
// STREAK INDICATOR SUB-COMPONENT
// ============================================

interface StreakIndicatorProps {
    history: TrainingHistoryItem[];
}

const StreakIndicator: React.FC<StreakIndicatorProps> = ({ history }) => {
    // Calculate current streak
    const calculateStreak = () => {
        // For demo, just count consecutive days
        return Math.min(history.length, 7);
    };

    const streak = calculateStreak();

    return (
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-hig-accent" />
                <span className="text-sm text-hig-text-secondary">
                    Current streak
                </span>
            </div>
            <div className="flex items-center gap-1">
                {[...Array(7)].map((_, idx) => (
                    <div
                        key={idx}
                        className={`
              w-3 h-3 rounded-sm
              ${idx < streak
                                ? 'bg-hig-accent'
                                : 'bg-hig-bg-tertiary border border-hig-border'
                            }
            `}
                    />
                ))}
                <span className="ml-2 text-sm font-semibold text-hig-text-primary">
                    {streak} day{streak !== 1 ? 's' : ''}
                </span>
            </div>
        </div>
    );
};

export default TrainingHistory;
